/**
 * Formats variable fullDetails object into flat key-value pairs for display
 * Handles nested objects, arrays, primitives, and edge cases
 */

export interface FormattedDetail {
	key: string
	value: string
	displayLabel: string
}

/**
 * Maximum depth for nested object traversal (prevents infinite recursion)
 */
const MAX_DEPTH = 3

/**
 * Maximum array items to display
 */
const MAX_ARRAY_ITEMS = 3

/**
 * Common technical acronyms that should remain uppercase
 */
const ACRONYMS = [
	'CPU', 'GPU', 'RAM', 'ROM', 'SSID', 'BSSID', 'MAC', 'IP', 'DNS', 'DHCP',
	'WAN', 'LAN', 'VPN', 'ONT', 'ONU', 'PON', 'GPON', 'EPON',
	'TX', 'RX', 'SNR', 'RSSI', 'RF', 'DBM', 'GHZ', 'MHZ',
	'RPM', 'PSU', 'UPS', 'LED', 'USB', 'HDMI', 'HTTP', 'HTTPS',
	'API', 'URL', 'URI', 'TCP', 'UDP', 'FTP', 'SSH', 'VLAN',
	'QOS', 'MTU', 'TTL', 'ISP', 'NAT', 'IGMP', 'STP', 'RSTP',
	'WIFI', 'WPA', 'WEP', 'WPS', 'NMS', 'SNMP', 'ICMP'
]

/**
 * Special casing for acronyms that aren't all-caps
 */
const ACRONYM_CASING: Record<string, string> = {
	'DBM': 'dBm',
	'GHZ': 'GHz',
	'MHZ': 'MHz',
	'WIFI': 'WiFi',
	'QOS': 'QoS'
}

/**
 * Converts a camelCase or PascalCase string to Title Case with spaces
 * Preserves common technical acronyms in uppercase
 *
 * @param str - The camelCase string to convert
 * @returns Title Case string with spaces
 *
 * @example
 * camelToTitleCase('signalStrength') // 'Signal Strength'
 * camelToTitleCase('cpuLoad') // 'CPU Load'
 * camelToTitleCase('wifiSSID') // 'WiFi SSID'
 */
function camelToTitleCase(str: string): string {
	// Split camelCase/PascalCase intelligently to preserve acronym sequences
	// "wifiSSID" → "wifi SSID", "HTTPSConnection" → "HTTPS Connection"
	const spaced = str
		.replace(/([a-z])([A-Z])/g, '$1 $2')           // lowercase followed by uppercase
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')    // acronym followed by capitalized word
		.trim()

	// Split into words
	const words = spaced.split(/[\s_-]+/)

	// Convert each word to title case, preserving acronyms
	const titleCased = words.map(word => {
		const upper = word.toUpperCase()

		// Check if word is a known acronym
		if (ACRONYMS.includes(upper)) {
			// Use special casing if defined, otherwise use uppercase
			return ACRONYM_CASING[upper] || upper
		}

		// Check if word is part of a compound acronym (e.g., "dBm")
		if (word.match(/^[a-z]?[A-Z]+[a-z]?$/)) {
			return word // Preserve mixed case for technical units
		}

		// Standard title case
		return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
	})

	return titleCased.join(' ')
}

/**
 * Formats a dot-notation key into a readable label
 * Handles nested paths and converts camelCase to Title Case
 *
 * @param key - The dot-notation key (e.g., 'diagnostics.fanSpeed')
 * @returns Formatted label (e.g., 'Diagnostics > Fan Speed')
 *
 * @example
 * formatKeyLabel('errorCode') // 'Error Code'
 * formatKeyLabel('diagnostics.fanSpeed') // 'Diagnostics > Fan Speed'
 * formatKeyLabel('config.wifi.ssid') // 'Config > WiFi > SSID'
 */
function formatKeyLabel(key: string): string {
	// Split on dots for nested keys
	const parts = key.split('.')

	// Convert each part to title case
	const formatted = parts.map(part => camelToTitleCase(part))

	// Join with separator
	return formatted.join(' > ')
}

/**
 * Formats a single value to a display string
 * @param value - The value to format
 * @returns Formatted string representation
 */
function formatValue(value: any): string {
	if (value === null || value === undefined) {
		return ''
	}

	if (typeof value === 'boolean') {
		return value ? 'true' : 'false'
	}

	if (typeof value === 'number') {
		return value.toString()
	}

	if (typeof value === 'string') {
		return value
	}

	if (Array.isArray(value)) {
		if (value.length === 0) {
			return '[]'
		}
		if (value.length <= MAX_ARRAY_ITEMS) {
			return `[${value.map(v => formatValue(v)).join(', ')}]`
		}
		const preview = value.slice(0, MAX_ARRAY_ITEMS).map(v => formatValue(v)).join(', ')
		return `[${preview}, ... +${value.length - MAX_ARRAY_ITEMS} more]`
	}

	if (typeof value === 'object') {
		// For nested objects, show a summary
		const keys = Object.keys(value)
		return `{${keys.length} ${keys.length === 1 ? 'property' : 'properties'}}`
	}

	return String(value)
}

/**
 * Recursively flattens a nested object into key-value pairs
 * @param obj - Object to flatten
 * @param prefix - Current key prefix for nested objects
 * @param depth - Current recursion depth
 * @returns Array of formatted details
 */
function flattenObject(
	obj: Record<string, any>,
	prefix: string = '',
	depth: number = 0
): FormattedDetail[] {
	const result: FormattedDetail[] = []

	// Prevent infinite recursion
	if (depth > MAX_DEPTH) {
		return result
	}

	// Get and sort keys
	const keys = Object.keys(obj).sort()

	for (const key of keys) {
		const value = obj[key]
		const fullKey = prefix ? `${prefix}.${key}` : key

		// Skip null/undefined values
		if (value === null || value === undefined) {
			continue
		}

		// Handle nested objects (but only if not too deep)
		if (typeof value === 'object' && !Array.isArray(value) && depth < MAX_DEPTH) {
			// Add nested object entries
			const nested = flattenObject(value, fullKey, depth + 1)
			result.push(...nested)
		} else {
			// Add primitive, array, or max-depth object
			const formattedValue = formatValue(value)
			if (formattedValue) {
				result.push({
					key: fullKey,
					value: formattedValue,
					displayLabel: formatKeyLabel(fullKey)
				})
			}
		}
	}

	return result
}

/**
 * Formats fullDetails object into displayable key-value pairs
 * Handles variable object structures, nested objects, arrays, and primitives
 *
 * @param fullDetails - The fullDetails object from device status response
 * @returns Array of formatted key-value pairs, sorted alphabetically by key
 *
 * @example
 * const details = {
 *   description: "Fiber ONT",
 *   signalStrength: "-23dBm",
 *   config: {
 *     wifi: { ssid: "MyNetwork" }
 *   }
 * }
 * const formatted = formatFullDetails(details)
 * // Returns:
 * // [
 * //   { key: "config.wifi.ssid", value: "MyNetwork" },
 * //   { key: "description", value: "Fiber ONT" },
 * //   { key: "signalStrength", value: "-23dBm" }
 * // ]
 */
export function formatFullDetails(
	fullDetails: Record<string, any> | null | undefined
): FormattedDetail[] {
	if (!fullDetails || typeof fullDetails !== 'object') {
		return []
	}

	try {
		return flattenObject(fullDetails)
	} catch (error) {
		// Handle edge cases like circular references
		console.error('Error formatting fullDetails:', error)
		return []
	}
}

/**
 * Checks if fullDetails has any displayable content
 * @param fullDetails - The fullDetails object to check
 * @returns True if there are displayable details
 */
export function hasDisplayableDetails(
	fullDetails: Record<string, any> | null | undefined
): boolean {
	return formatFullDetails(fullDetails).length > 0
}
